/**
 * Zentrale Auflösung der Benutzerrolle.
 *
 * WICHTIG: Die Rolle wird ausschließlich aus `app_metadata` gelesen.
 * `user_metadata` ist in Supabase vom Nutzer selbst beschreibbar
 * (`supabase.auth.updateUser({ data: { role: 'gm' } })`) und darf deshalb
 * niemals über Rechte entscheiden. Die Datenbank prüft dieselbe Quelle über
 * `public.jwt_role()` – siehe supabase/migrationen/001_rollen_eskalation.sql.
 *
 * Diese Prüfungen im Frontend sind reine Bequemlichkeit (Menüs ausblenden,
 * früh umleiten). Verbindlich sind die RLS-Policies der Datenbank.
 *
 * Vier Rollen, Entscheidung F-15:
 *
 *   super_admin    – Entwicklung und Notfälle
 *   gm             – Spielleitung
 *   dungeon_assist – Assistenz der Spielleitung
 *   player         – Spieler
 */

/** Die gültigen Rollen, aufsteigend nach Rechten. */
export const ROLLEN = ['player', 'dungeon_assist', 'gm', 'super_admin'] as const;

export type Rolle = (typeof ROLLEN)[number];

/** Rollen-Hierarchie: höhere Zahl = mehr Rechte. */
export const ROLE_HIERARCHY: Record<Rolle, number> = {
  player: 0,
  dungeon_assist: 1,
  gm: 2,
  super_admin: 3,
};

/** Anzeigenamen für Auswahlfelder. */
export const ROLLEN_BESCHRIFTUNG: Record<Rolle, string> = {
  player: 'Spieler',
  dungeon_assist: 'DM-Assistent',
  gm: 'Spielleitung',
  super_admin: 'Super-Admin',
};

/**
 * Bringt eine Rollenangabe auf eine der vier gültigen Formen.
 *
 * `admin` gibt es nicht mehr (F-15), wird aber weiterhin als `gm` gelesen.
 * Das ist Absicht und nicht Nachlässigkeit: Bis alle Konten über
 * `004_rollenmodell.sql` umgezogen sind und jeder ein frisches Token hat,
 * tragen Bestandsanmeldungen noch `admin`. Ohne diese Umdeutung würde die
 * Spielleitung zwischen Frontend-Rollout und Datenbank-Migration ausgesperrt.
 */
export const normalisiereRolle = (roh: unknown): Rolle | null => {
  if (typeof roh !== 'string') return null;

  const normalisiert = roh.trim().toLowerCase();
  if (!normalisiert.length) return null;

  if (normalisiert === 'admin') return 'gm';

  return (ROLLEN as readonly string[]).includes(normalisiert) ? (normalisiert as Rolle) : null;
};

/**
 * Liest die Rolle eines Supabase-Users.
 *
 * @returns Eine der vier Rollen, oder null wenn keine gültige gesetzt ist.
 */
export const resolveUserRole = (user: unknown): Rolle | null => {
  const metadaten = (user as { app_metadata?: { role?: unknown } } | null | undefined)
    ?.app_metadata;
  return normalisiereRolle(metadaten?.role);
};

/** Prüft, ob eine Rolle mindestens der geforderten Stufe entspricht. */
export const hasRoleAtLeast = (rolle: unknown, geforderteRolle: unknown): boolean => {
  const aktuell = ROLE_HIERARCHY[normalisiereRolle(rolle) ?? 'player'];
  const noetig = ROLE_HIERARCHY[normalisiereRolle(geforderteRolle) ?? 'player'];
  return aktuell >= noetig;
};

/**
 * Darf die Runde leiten: alles ändern, was den Meisterbereich betrifft.
 *
 * Entspricht `public.is_admin()` in der Datenbank.
 */
export const istSpielleitung = (rolle: unknown): boolean => hasRoleAtLeast(rolle, 'gm');

/**
 * Gehört zum Meisterteam, Assistenz eingeschlossen.
 *
 * Entspricht `public.is_gm_team()`. Für Ansichten gedacht, die mehr zeigen als
 * einem Spieler zusteht, ohne Schreibrechte zu erfordern.
 */
export const istMeisterTeam = (rolle: unknown): boolean => hasRoleAtLeast(rolle, 'dungeon_assist');

/** Nur für Entwicklung und Notfälle. */
export const istSuperAdmin = (rolle: unknown): boolean =>
  normalisiereRolle(rolle) === 'super_admin';
